import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  type Dispatch,
  type ReactNode,
} from 'react';

export const FilterActionKind = {
  Set: 'SET',
  Reset: 'RESET',
  Sync: 'SYNC',
} as const;

export type FilterSideEffect<TState> = (state: TState | undefined) => void;

export type FilterAction<TState> =
  | {
      type: typeof FilterActionKind.Set;
      payload: Partial<TState>;
      sideEffect?: FilterSideEffect<TState>;
    }
  | {
      type: typeof FilterActionKind.Reset;
      sideEffect?: FilterSideEffect<TState>;
    }
  | {
      type: typeof FilterActionKind.Sync;
      payload: TState;
    };

export type FilterContextValue<TState> = {
  state: TState;
  dispatch: Dispatch<FilterAction<TState>>;
};

type FilterStateTransformer<TState> = (state: TState) => TState;

export type CreateFilterContextOptions<
  TState extends Record<string, unknown>,
  TSearch,
> = {
  parseSearch: (search: string) => TSearch;
  getInitialState: (searchParams: TSearch) => TState;
  cleanUp?: FilterStateTransformer<TState>;
  normalize?: FilterStateTransformer<TState>;
  createResetState?: () => TState;
  getResetSideEffectState?: (state: TState) => TState | undefined;
};

const identity = <TValue,>(value: TValue): TValue => value;

const createEmptyState = <TState extends Record<string, unknown>>(): TState =>
  ({}) as TState;

export const createFilterContext = <
  TState extends Record<string, unknown>,
  TSearch,
>({
  parseSearch,
  getInitialState,
  cleanUp = identity<TState>,
  normalize = identity<TState>,
  createResetState = createEmptyState<TState>,
  getResetSideEffectState,
}: CreateFilterContextOptions<TState, TSearch>) => {
  const FilterContext = createContext<FilterContextValue<TState> | undefined>(
    undefined,
  );

  const filterReducer = (
    state: TState,
    action: FilterAction<TState>,
  ): TState => {
    switch (action.type) {
      case FilterActionKind.Set: {
        const newState = normalize(
          cleanUp({
            ...state,
            ...action.payload,
          } as TState),
        );

        action.sideEffect?.(newState);

        return newState;
      }
      case FilterActionKind.Reset: {
        const resetState = createResetState();

        action.sideEffect?.(getResetSideEffectState?.(resetState));

        return resetState;
      }
      case FilterActionKind.Sync: {
        return normalize(cleanUp(action.payload));
      }
      default: {
        throw new Error('Unhandled action type');
      }
    }
  };

  const FilterProvider = ({ children }: { children: ReactNode }) => {
    const searchParams = parseSearch(window.location.search);
    const initialValues = normalize(getInitialState(searchParams));

    const [state, dispatch] = useReducer(filterReducer, initialValues);

    // Browser back/forward navigation changes the URL without going through
    // dispatch, so the state would otherwise get out of sync with the URL.
    useEffect(() => {
      const handlePopState = () => {
        const nextSearchParams = parseSearch(window.location.search);
        dispatch({
          type: FilterActionKind.Sync,
          payload: getInitialState(nextSearchParams),
        });
      };

      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const value = { state, dispatch };
    return (
      <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
    );
  };

  const useFilterContext = (): FilterContextValue<TState> => {
    const context = useContext(FilterContext);
    if (context === undefined) {
      throw new Error('useFilterContext must be used within a FilterProvider');
    }
    return context;
  };

  return {
    FilterProvider,
    useFilterContext,
  };
};
